import { LANDING_PAGE_REDIRECT } from "@/abilities/shop/config/constants";
import { useShopStatusStore } from "@/abilities/shop/features/shop-core/store/shop-status.store";
import { FdiShop } from "@/abilities/shop/types/fdi-shop";
import { useHasAccessAsync } from "@/shared/access-control/composables/use-has-access-async";
import { useMySourceProviderLazyQuery } from "@/shared/services/graphql/generated/consumer-graph-types";
import { ref } from "vue";

// Create a singleton token store that persists across component lifecycles
const token = ref<FdiShop.Token | undefined>(undefined);

export function useFdiAuth() {
    const connectToShop = async () => {
        const store = useShopStatusStore();
        const { baseUrl } = store.fdiShopConfig() ?? {};

        if (!baseUrl || !store.isShopOnline) {
            return;
        }

        const loginOauthParam = await getAuthSpecificUrlOAuthParam();
        const loginURL = new URL(`${loginOauthParam}login`, baseUrl);
        const loginLandingPage = new URL(LANDING_PAGE_REDIRECT, window.location.origin);
        const callbackCurrentRouteAsString = window.location.pathname + window.location.search;

        loginLandingPage.searchParams.append("callingPage", callbackCurrentRouteAsString);
        loginURL.searchParams.append("redirectto", loginLandingPage.href);

        window.location.href = loginURL.href;

        // Wait for redirect to complete
        await waitForRedirect(loginURL.href);
    };

    const getToken = async (code: string) => {
        const store = useShopStatusStore();
        const { baseUrl } = store.fdiShopConfig() ?? {};

        if (!baseUrl) {
            throw new Error("Base URL is not defined");
        }

        const loginOauthParam = await getAuthSpecificUrlOAuthParam();
        const getTokenUrl = new URL(`${loginOauthParam}token`, baseUrl);
        getTokenUrl.searchParams.append("code", code);

        const tokenResponse = await fetch(getTokenUrl.href);
        if (tokenResponse.status === 401) throw new Error("401");
        if (tokenResponse.status === 403) throw new Error("403");
        if (!tokenResponse.ok) throw new Error(tokenResponse.statusText);

        token.value = await tokenResponse.json();
        return token.value;
    };

    const getCurrentToken = () => {
        return token.value;
    };

    const waitForRedirect = async (targetUrl: string, maxAttempts = 10) => {
        for (let i = 0; i < maxAttempts; i++) {
            if (window.location.href !== targetUrl) {
                await new Promise((resolve) => setTimeout(resolve, 250));
            } else {
                break;
            }
        }
    };

    /**
     * TODO: remove with QIT-6347!!
     *
     * @returns only for fresenius, as fdi connector does not provide connections for multiple idps yet
     */
    async function getAuthSpecificUrlOAuthParam() {
        const isAuthSpecificUrlNeeded = await useHasAccessAsync({
            featureID: "fresenius_shop_idps",
        });

        if (!isAuthSpecificUrlNeeded) {
            return "oauth/";
        }

        const { load } = useMySourceProviderLazyQuery();
        const sourceProvider = await load();

        if (sourceProvider === false || sourceProvider.me.sourceProvider?.length === 0) {
            return "oauth/";
        }

        const provider = sourceProvider.me.sourceProvider?.[0];
        if (provider === "espcauth") {
            return "oauth/synergy/";
        } else if (provider === "espcentraid") {
            return "oauth/entraid/";
        } else {
            return "oauth/";
        }
    }

    return {
        connectToShop,
        getToken,
        getCurrentToken,
    };
}
